import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="ai-prompt-helper"
export default class extends Controller {
  static targets = ["promptField", "generateButton", "optimizeButton"]
  static values = { 
    generateUrl: String,
    optimizeUrl: String
  }

  connect() {
    this.boundUpdateButtonStates = () => this.updateButtonStates()
    this.updateButtonStates()
    if (this.hasPromptFieldTarget) {
      this.promptFieldTarget.addEventListener('input', this.boundUpdateButtonStates)
    }
  }

  disconnect() {
    if (this.hasPromptFieldTarget && this.boundUpdateButtonStates) {
      this.promptFieldTarget.removeEventListener('input', this.boundUpdateButtonStates)
    }
  }

  // Generate a new AI prompt based on form data
  async generatePrompt(event) {
    event.preventDefault()
    
    if (!this.generateUrlValue) {
      this.showError("Generate URL not configured")
      return
    }

    const formData = this.collectFormData()
    
    try {
      this.setLoadingState(this.generateButtonTarget, true)
      
      const response = await fetch(this.generateUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(formData)
      })

      const data = await response.json()

      if (data.success) {
        this.promptFieldTarget.value = data.prompt
        this.promptFieldTarget.dispatchEvent(new Event('input', { bubbles: true }))
        this.showSuccess(data.message)
        this.updateButtonStates()
      } else {
        this.showError(data.message || 'Failed to generate prompt')
      }
    } catch (error) {
      console.error('Error generating prompt:', error)
      this.showError('Network error. Please check your connection and try again.')
    } finally {
      this.setLoadingState(this.generateButtonTarget, false)
    }
  }

  // Optimize existing AI prompt
  async optimizePrompt(event) {
    event.preventDefault()
    
    if (!this.optimizeUrlValue) {
      this.showError("Optimize URL not configured")
      return
    }

    const existingPrompt = this.promptFieldTarget.value.trim()
    if (!existingPrompt) {
      this.showError("Please enter a prompt first before optimizing")
      return
    }

    const formData = this.collectFormData()
    formData.existing_prompt = existingPrompt

    try {
      this.setLoadingState(this.optimizeButtonTarget, true)
      
      const response = await fetch(this.optimizeUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(formData)
      })

      const data = await response.json()

      if (data.success) {
        this.promptFieldTarget.value = data.prompt
        this.promptFieldTarget.dispatchEvent(new Event('input', { bubbles: true }))
        this.showSuccess(data.message)
      } else {
        this.showError(data.message || 'Failed to optimize prompt')
      }
    } catch (error) {
      console.error('Error optimizing prompt:', error)
      this.showError('Network error. Please check your connection and try again.')
    } finally {
      this.setLoadingState(this.optimizeButtonTarget, false)
    }
  }

  // Collect form data for AI processing
  collectFormData() {
    const form = this.element.closest('form')
    const formData = new FormData(form)
    
    // Convert FormData to nested object structure
    const eventData = {}
    
    // Basic event fields
    eventData.title = formData.get('event[title]') || ''
    eventData.theme = formData.get('event[theme]') || ''
    eventData.budget = formData.get('event[budget]') || ''
    eventData.guest_count = formData.get('event[guest_count]') || ''
    eventData.location = formData.get('event[location]') || ''
    eventData.event_date = formData.get('event[event_date]') || ''
    
    // Selected categories (multiple values)
    const selectedCategories = formData.getAll('event[planning_preferences][selected_categories][]')
    eventData.planning_preferences = {
      selected_categories: selectedCategories.filter(cat => cat && cat.trim())
    }

    return { event: eventData }
  }

  // Update button states based on prompt field content
  updateButtonStates() {
    if (!this.hasPromptFieldTarget) return
    
    const hasContent = this.promptFieldTarget.value.trim().length > 0
    
    if (this.hasGenerateButtonTarget) {
      // Show generate button when field is empty
      this.generateButtonTarget.style.display = hasContent ? 'none' : 'inline-flex'
    }
    
    if (this.hasOptimizeButtonTarget) {
      // Show optimize button when field has content
      this.optimizeButtonTarget.style.display = hasContent ? 'inline-flex' : 'none'
    }
  }

  // Set loading state for button
  setLoadingState(button, loading) {
    if (!button) return

    const originalText = button.dataset.originalText || button.textContent
    button.dataset.originalText = originalText

    if (loading) {
      button.disabled = true
      button.innerHTML = `
        <svg class="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Working...
      `
    } else {
      button.disabled = false
      button.textContent = originalText
    }
  }

  // Show success message
  showSuccess(message) {
    this.showFlashMessage(message, 'success')
  }

  // Show error message  
  showError(message) {
    this.showFlashMessage(message, 'error')
  }

  // Show flash message using the existing flash system
  showFlashMessage(message, type) {
    // Create a temporary flash message element
    const flashContainer = document.getElementById('flash_messages')
    if (!flashContainer) {
      console.warn('Flash messages container not found')
      return
    }

    // Create flash message HTML matching the app's flash structure
    const flashId = `flash_${Date.now()}`
    const iconClass = type === 'success' ? 'ph-fill ph-check-circle' : 'ph-fill ph-warning'
    const bgClass = type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
    const textClass = type === 'success' ? 'text-green-800' : 'text-red-800'
    const iconColorClass = type === 'success' ? 'text-green-400' : 'text-red-400'

    const flashHTML = `
      <div id="${flashId}" class="flash-message rounded-lg border ${bgClass} p-4 mb-4" data-controller="flash" data-flash-auto-dismiss-value="true">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <i class="${iconClass} ${iconColorClass} text-lg"></i>
          </div>
          <div class="ml-3 flex-1">
            <p class="${textClass} text-sm font-medium">${message}</p>
          </div>
          <div class="ml-3 flex-shrink-0">
            <button type="button" class="${textClass} hover:${textClass.replace('800', '900')} focus:outline-none" data-action="click->flash#dismiss">
              <i class="ph-bold ph-x text-sm"></i>
            </button>
          </div>
        </div>
      </div>
    `

    // Add the flash message
    flashContainer.insertAdjacentHTML('afterbegin', flashHTML)
    
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      const flashElement = document.getElementById(flashId)
      if (flashElement) {
        flashElement.remove()
      }
    }, 5000)
  }
}